import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Clock, Eye, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { securityAPI } from "../services/api";

const severityVariant = {
  low: "secondary",
  medium: "outline",
  high: "destructive",
  critical: "destructive",
};

const statusIcon = {
  open: AlertTriangle,
  acknowledged: Eye,
  resolved: CheckCircle2,
};

const formatDateTime = (value, locale) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const toDateTimeParam = (value, endOfDay = false) => {
  if (!value) return undefined;
  return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`;
};

const compactJson = (value) => {
  if (!value) return "-";
  const text = JSON.stringify(value);
  return text.length > 90 ? `${text.slice(0, 90)}...` : text;
};

const StatCard = ({ title, value, icon: Icon, tone }) => (
  <Card className="rounded-lg">
    <CardContent className="flex items-center justify-between p-5">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-semibold">{value}</p>
      </div>
      <div className={`rounded-lg p-3 ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
    </CardContent>
  </Card>
);

const EMPTY_SUMMARY = { total_audit_logs: 0, open_security_events: 0, failed_logins: 0, high_risk_events: 0 };

const SecurityCenter = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    user: "",
    severity: "",
    eventType: "",
  });

  const apiParams = useMemo(() => ({
    start_date: toDateTimeParam(filters.startDate),
    end_date: toDateTimeParam(filters.endDate, true),
    user: filters.user || undefined,
    severity: filters.severity || undefined,
    event_type: filters.eventType || undefined,
  }), [filters]);

  const [summaryQ, auditQ, loginQ, eventsQ] = useQueries({
    queries: [
      {
        queryKey: ["security-summary"],
        queryFn: () => securityAPI.getSummary(),
      },
      {
        queryKey: ["security-audit", apiParams],
        queryFn: () => securityAPI.getAuditLogs(apiParams).then((r) => Array.isArray(r) ? r : []),
      },
      {
        queryKey: ["security-login-history", apiParams],
        queryFn: () => securityAPI.getLoginHistory(apiParams).then((r) => Array.isArray(r) ? r : []),
      },
      {
        queryKey: ["security-events", apiParams],
        queryFn: () => securityAPI.getEvents(apiParams).then((r) => Array.isArray(r) ? r : []),
      },
    ],
  });

  const summary = summaryQ.data ?? EMPTY_SUMMARY;
  const auditLogs = auditQ.data ?? [];
  const loginHistory = loginQ.data ?? [];
  const events = eventsQ.data ?? [];
  const loading = summaryQ.isPending || auditQ.isPending || loginQ.isPending || eventsQ.isPending;

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["security-summary"] });
    queryClient.invalidateQueries({ queryKey: ["security-audit"] });
    queryClient.invalidateQueries({ queryKey: ["security-login-history"] });
    queryClient.invalidateQueries({ queryKey: ["security-events"] });
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const acknowledgeEvent = async (eventId) => {
    try {
      await securityAPI.acknowledgeEvent(eventId);
      toast.success(t("securityCenter.acked"));
      queryClient.invalidateQueries({ queryKey: ["security-events"] });
      queryClient.invalidateQueries({ queryKey: ["security-summary"] });
    } catch {
      toast.error(t("securityCenter.ackError"));
    }
  };

  const resolveEvent = async (eventId) => {
    try {
      await securityAPI.resolveEvent(eventId);
      toast.success(t("securityCenter.resolved"));
      queryClient.invalidateQueries({ queryKey: ["security-events"] });
      queryClient.invalidateQueries({ queryKey: ["security-summary"] });
    } catch {
      toast.error(t("securityCenter.resolveError"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("securityCenter.title")}</h1>
          <p className="text-muted-foreground">{t("securityCenter.subtitle")}</p>
        </div>
        <Button onClick={refreshAll} disabled={loading} variant="outline">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {t("common.refresh")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title={t("securityCenter.statTotalAudit")} value={summary.total_audit_logs} icon={Clock} tone="bg-muted text-muted-foreground" />
        <StatCard title={t("securityCenter.statOpenEvents")} value={summary.open_security_events} icon={ShieldAlert} tone="bg-amber-950/30 text-amber-400" />
        <StatCard title={t("securityCenter.statFailedLogins")} value={summary.failed_logins} icon={XCircle} tone="bg-red-950/30 text-red-400" />
        <StatCard title={t("securityCenter.statHighRisk")} value={summary.high_risk_events} icon={AlertTriangle} tone="bg-purple-950/30 text-purple-400" />
      </div>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle className="text-lg">{t("securityCenter.filters")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-5">
          <Input type="date" value={filters.startDate} onChange={(event) => updateFilter("startDate", event.target.value)} />
          <Input type="date" value={filters.endDate} onChange={(event) => updateFilter("endDate", event.target.value)} />
          <Input placeholder={t("securityCenter.userPlaceholder")} value={filters.user} onChange={(event) => updateFilter("user", event.target.value)} />
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={filters.severity}
            onChange={(event) => updateFilter("severity", event.target.value)}
          >
            <option value="">{t("securityCenter.allSeverities")}</option>
            <option value="low">{t("securityCenter.sevLow")}</option>
            <option value="medium">{t("securityCenter.sevMedium")}</option>
            <option value="high">{t("securityCenter.sevHigh")}</option>
            <option value="critical">{t("securityCenter.sevCritical")}</option>
          </select>
          <Input placeholder={t("securityCenter.eventTypePlaceholder")} value={filters.eventType} onChange={(event) => updateFilter("eventType", event.target.value)} />
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle className="text-lg">{t("securityCenter.eventsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("securityCenter.colDate")}</TableHead>
                <TableHead>{t("securityCenter.colSeverity")}</TableHead>
                <TableHead>{t("securityCenter.colType")}</TableHead>
                <TableHead>{t("securityCenter.colDescription")}</TableHead>
                <TableHead>{t("securityCenter.colStatus")}</TableHead>
                <TableHead className="text-right">{t("securityCenter.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => {
                const StatusIcon = statusIcon[event.status] || AlertTriangle;
                return (
                  <TableRow key={event.id}>
                    <TableCell>{formatDateTime(event.created_at, i18n.language)}</TableCell>
                    <TableCell><Badge variant={severityVariant[event.severity] || "secondary"}>{event.severity}</Badge></TableCell>
                    <TableCell className="font-medium">{event.event_type}</TableCell>
                    <TableCell className="max-w-md">{event.description}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 text-sm">
                        <StatusIcon className="h-4 w-4" />
                        {event.status}
                      </span>
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button size="sm" variant="outline" disabled={event.status === "resolved"} onClick={() => acknowledgeEvent(event.id)}>
                        {t("securityCenter.acknowledge")}
                      </Button>
                      <Button size="sm" disabled={event.status === "resolved"} onClick={() => resolveEvent(event.id)}>
                        {t("securityCenter.resolve")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("securityCenter.noEvents")}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="text-lg">{t("securityCenter.auditTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("securityCenter.colDate")}</TableHead>
                  <TableHead>{t("securityCenter.colActor")}</TableHead>
                  <TableHead>{t("securityCenter.colAction")}</TableHead>
                  <TableHead>{t("securityCenter.colEntity")}</TableHead>
                  <TableHead>{t("securityCenter.colIp")}</TableHead>
                  <TableHead>{t("securityCenter.colDiff")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{formatDateTime(log.created_at, i18n.language)}</TableCell>
                    <TableCell>{log.actor_email || "-"}</TableCell>
                    <TableCell className="font-medium">{log.action}</TableCell>
                    <TableCell>{log.entity_type || "-"} {log.entity_id ? `#${log.entity_id}` : ""}</TableCell>
                    <TableCell>{log.ip_address || "-"}</TableCell>
                    <TableCell className="max-w-xs text-xs text-muted-foreground">{compactJson(log.new_value)}</TableCell>
                  </TableRow>
                ))}
                {auditLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("securityCenter.noAudit")}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="text-lg">{t("securityCenter.loginTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("securityCenter.colDate")}</TableHead>
                  <TableHead>{t("securityCenter.colEmail")}</TableHead>
                  <TableHead>{t("securityCenter.colResult")}</TableHead>
                  <TableHead>{t("securityCenter.colReason")}</TableHead>
                  <TableHead>{t("securityCenter.colIp")}</TableHead>
                  <TableHead>{t("securityCenter.colUserAgent")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loginHistory.map((login) => (
                  <TableRow key={login.id}>
                    <TableCell>{formatDateTime(login.created_at, i18n.language)}</TableCell>
                    <TableCell>{login.email}</TableCell>
                    <TableCell>
                      <Badge variant={login.success ? "secondary" : "destructive"}>{login.success ? t("securityCenter.success") : t("securityCenter.failed")}</Badge>
                    </TableCell>
                    <TableCell>{login.failure_reason || "-"}</TableCell>
                    <TableCell>{login.ip_address || "-"}</TableCell>
                    <TableCell className="max-w-xs truncate">{login.user_agent || "-"}</TableCell>
                  </TableRow>
                ))}
                {loginHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("securityCenter.noHistory")}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityCenter;
